import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./album-sidebar.css";
import constants from "../../const";

const SCROLL_STORAGE_KEY = 'gallerySidebarScrollTop';

function AlbumSidebar(props) {
    const [isOpen, setIsOpen] = useState(() => localStorage.getItem('gallerySidebarOpen') !== 'false');
    useEffect(() => {
        localStorage.setItem('gallerySidebarOpen', String(isOpen));
    }, [isOpen]);
    const [isExpanded, setIsExpanded] = useState(() => localStorage.getItem('gallerySidebarExpanded') === 'true');
    useEffect(() => {
        localStorage.setItem('gallerySidebarExpanded', String(isExpanded));
    }, [isExpanded]);
    const [albumSearch, setAlbumSearch] = useState('');
    const [hoverPreview, setHoverPreview] = useState(null);
    const contentRef = useRef(null);
    const pendingScrollRestoreRef = useRef(true);

    const handleToggle = () => {
        if (!isOpen) {
            pendingScrollRestoreRef.current = true;
        }
        setIsOpen(!isOpen);
    };

    const handleExpandToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const isAlbumActive = (albumName) => {
        return props.currentAlbum === albumName;
    };

    const albumEntries = (props.albums ? props.albums : Object.entries(props.images?.children || {})).map((item) => {
        // Handle both array format (from refreshed albums) and object format (from props.images)
        if (Array.isArray(props.albums) && props.albums.length > 0) {
            // Format from API: {folder_name, title, image_count, ...}
            return { albumName: item.folder_name, title: item.title || '', imageCount: item.image_count || 0 };
        }
        // Format from props.images?.children: [albumName, albumData]
        const [albumName, albumData] = item;
        return { albumName, title: '', imageCount: albumData?.contents?.length || 0 };
    });

    const searchTerm = albumSearch.trim().toLowerCase();
    const visibleAlbums = albumEntries.filter(({ albumName, title }) =>
        !searchTerm ||
        albumName.toLowerCase().includes(searchTerm) ||
        title.toLowerCase().includes(searchTerm)
    );

    // Albums load asynchronously, so restore the scroll position only after the list has rendered.
    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el || !pendingScrollRestoreRef.current || albumEntries.length === 0) return;
        el.scrollTop = Number(localStorage.getItem(SCROLL_STORAGE_KEY)) || 0;
        pendingScrollRestoreRef.current = false;
    });

    const handleContentScroll = (e) => {
        if (pendingScrollRestoreRef.current) return;
        localStorage.setItem(SCROLL_STORAGE_KEY, String(e.currentTarget.scrollTop));
    };

    const getPreviewUrl = (albumName) => {
        const token = `?token=${localStorage.getItem('token')?.slice(7) || ''}`;
        if (albumName === 'Pictures') {
            return `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${props.album}/preview.webp${token}`;
        }
        return `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${props.album}/${encodeURIComponent(albumName)}/preview.webp${token}`;
    };

    const PREVIEW_HEIGHT = 450;
    const showHoverPreview = (e, src, alt) => {
        const itemRect = e.currentTarget.getBoundingClientRect();
        const sidebarRect = e.currentTarget.closest('.album-sidebar').getBoundingClientRect();
        setHoverPreview({
            src,
            alt,
            left: sidebarRect.right + 12,
            top: Math.max(12, Math.min(itemRect.top, window.innerHeight - PREVIEW_HEIGHT - 12)),
        });
    };
    const hideHoverPreview = () => setHoverPreview(null);

    return (
        <div className={`album-sidebar ${isOpen ? 'open' : 'closed'} ${isExpanded ? 'expanded' : ''}`}>
            {hoverPreview && (
                <img
                    className="album-hover-preview"
                    src={hoverPreview.src}
                    alt={hoverPreview.alt}
                    style={{ top: hoverPreview.top, left: hoverPreview.left }}
                />
            )}
            <div className="sidebar-header">
                <h4 className="sidebar-title">Albums</h4>
                <div className="sidebar-buttons">
                    <button
                        className="sidebar-expand-btn"
                        onClick={handleExpandToggle}
                        title={isExpanded ? "Collapse previews" : "Expand previews"}
                    >
                        {isExpanded ? '◀' : '▶'}
                    </button>
                    <button
                        className="sidebar-toggle-btn"
                        onClick={handleToggle}
                        title={isOpen ? "Collapse" : "Expand"}
                    >
                        {isOpen ? '✕' : '☰'}
                    </button>
                </div>
            </div>

            {isOpen && (
                <div className="sidebar-content" ref={contentRef} onScroll={handleContentScroll}>
                    <div className="album-search">
                        <input
                            type="search"
                            placeholder="Search albums..."
                            value={albumSearch}
                            onChange={(e) => setAlbumSearch(e.target.value)}
                            aria-label="Search albums"
                        />
                    </div>
                    {albumEntries.length > 0 && visibleAlbums.length === 0 && (
                        <div className="album-search-empty">No albums match "{albumSearch}"</div>
                    )}
                    <div className="album-list">
                        {/* All Pictures link */}
                        <Link
                            to={`/gallery/${encodeURIComponent(props.album)}?album=Pictures`}
                            className={`album-item ${isAlbumActive('Pictures') ? 'active' : ''}`}
                            style={{ color: "black", textDecoration: "none" }}
                            onMouseEnter={(e) => showHoverPreview(e, getPreviewUrl('Pictures'), 'All Pictures')}
                            onMouseLeave={hideHoverPreview}
                        >
                            <div className="album-item-preview">
                                <img
                                    src={getPreviewUrl('Pictures')}
                                    alt="All Pictures"
                                    className="album-thumbnail"
                                />
                            </div>
                            {!isExpanded && (
                                <div className="album-item-info">
                                    <span className="album-item-name">📸 All Pictures</span>
                                    <span className="album-item-count">
                                        {props.images?.contents?.length || 0}
                                    </span>
                                </div>
                            )}
                        </Link>
                        {isExpanded && (
                            <div className="album-item-label">
                                <span className="album-item-name">📸 All Pictures</span>
                                <span className="album-item-count">
                                    {props.images?.contents?.length || 0}
                                </span>
                            </div>
                        )}

                        {/* Other albums */}
                        {visibleAlbums.map(({ albumName, imageCount }) => {
                            return (
                                <div key={albumName}>
                                    <Link
                                        to={`/gallery/${encodeURIComponent(props.album)}?album=${encodeURIComponent(albumName)}`}
                                        className={`album-item ${isAlbumActive(albumName) ? 'active' : ''}`}
                                        style={{ color: "black", textDecoration: "none" }}
                                        onMouseEnter={(e) => showHoverPreview(e, getPreviewUrl(albumName), albumName)}
                                        onMouseLeave={hideHoverPreview}
                                    >
                                        <div className="album-item-preview">
                                            <img
                                                src={getPreviewUrl(albumName)}
                                                alt={albumName}
                                                className="album-thumbnail"
                                            />
                                        </div>
                                        {!isExpanded && (
                                            <div className="album-item-info">
                                                <span className="album-item-name">📁 {albumName}</span>
                                                <span className="album-item-count">
                                                    {imageCount}
                                                </span>
                                            </div>
                                        )}
                                    </Link>
                                    {isExpanded && (
                                        <div className="album-item-label">
                                            <span className="album-item-name">📁 {albumName}</span>
                                            <span className="album-item-count">
                                                {imageCount}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AlbumSidebar;
